import { ReactNode, useCallback, useEffect, useState } from "react";
import { cn } from "@/core/helpers/cn";
import { useKeyPress } from "@/core/keyPressManager";
import ActionButton from "@/shared/ui/ActionButton";
import KeyCode from "@/assets/constants/key-code";

export type KeyAction = {
  label: string;
  keyName: string;
  className?: string;
  classNameAction?: string;
  action: () => Promise<void> | void;
  color: "red" | "green";
  onClick?: () => void;
};

type Props = {
  name: string;
  actions: Map<KeyCode, KeyAction>;
  className?: string;
  children?: ReactNode;
};

function KeyActionPanel({ name, actions, className, children }: Props) {
  const { subscribeKeyDown, subscribeKeyUp, unsubscribeKeyDown, unsubscribeKeyUp } = useKeyPress();
  const [activeKey, setActiveKey] = useState<number | null>(null);

  const handleKeyDown = useCallback((keyCode: number) => {
    setActiveKey(keyCode);
  }, []);

  const handleKeyUp = useCallback(
    (keyCode: number) => {
      const action = actions.get(keyCode);
      if (action) {
        action.action();
      }
      setActiveKey(null);
    },
    [actions],
  );

  useEffect(() => {
    const key = `key-action-handler-${name}`;
    subscribeKeyDown(key, handleKeyDown);
    subscribeKeyUp(key, handleKeyUp);
    return () => {
      unsubscribeKeyDown(key);
      unsubscribeKeyUp(key);
    };
  }, [
    handleKeyUp,
    handleKeyDown,
    subscribeKeyDown,
    subscribeKeyUp,
    unsubscribeKeyDown,
    unsubscribeKeyUp,
  ]);

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      {actions &&
        Array.from(actions.entries()).map(([keyCode, { keyName, ...rest }]) => (
          <ActionButton
            key={keyCode}
            {...rest}
            action={keyName}
            isActive={activeKey === keyCode}
            onClick={() => {
              actions.get(keyCode)?.action();
              actions.get(keyCode)?.onClick?.();
            }}
          />
        ))}
      {children}
    </div>
  );
}

export default KeyActionPanel;
